import { useState } from 'react'
import { Loader2, GitBranch } from 'lucide-react'
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/Checkbox'
import { useAppDispatch } from '@/redux/hooks'
import { createRenegotiationRequest } from '../../redux/asyncThunks/charterThunks'
import { toast } from 'sonner'

const AMENDABLE_SECTIONS = [
  { key: 'project_overview', label: 'Project Overview' },
  { key: 'communication_plan', label: 'Communication Plan' },
  { key: 'deliverables_and_deadlines', label: 'Deliverables & Deadlines' },
  { key: 'roles_and_responsibilities', label: 'Roles & Responsibilities' },
  { key: 'conflict_resolution', label: 'Conflict Resolution' },
  { key: 'team_boundaries', label: 'Team Boundaries' },
  { key: 'operations', label: 'Operations' },
  { key: 'leadership_structure', label: 'Leadership Structure' },
  { key: 'resources_and_support', label: 'Resources & Support' },
]

interface AmendmentProposalFormProps {
  roomId: string
  teamId: number
  /** Called after a successful submit (host closes/returns). */
  onSubmitted: () => void
  /** Called when the user cancels/backs out. */
  onCancel: () => void
}

/**
 * Presentational amendment form (no Dialog wrapper) so it can be embedded
 * directly inside another dialog's content — avoids a Radix Dialog-in-Dialog.
 */
export function AmendmentProposalForm({
  roomId,
  teamId,
  onSubmitted,
  onCancel,
}: AmendmentProposalFormProps) {
  const dispatch = useAppDispatch()
  const [selectedSections, setSelectedSections] = useState<string[]>([])
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const toggleSection = (key: string) => {
    setSelectedSections((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  const handleSubmit = async () => {
    if (!selectedSections.length || !reason.trim()) return
    setIsSubmitting(true)
    try {
      await dispatch(
        createRenegotiationRequest({
          roomId,
          teamId,
          sections_to_amend: selectedSections,
          reason: reason.trim(),
        })
      )
      toast.success(
        'Amendment request submitted! Your team will be notified to vote.',
        { duration: 5000 }
      )
      setSelectedSections([])
      setReason('')
      onSubmitted()
    } catch {
      toast.error('Failed to submit amendment request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-2'>
          <GitBranch className='h-5 w-5 text-amber-600' />
          Propose Charter Amendment
        </DialogTitle>
        <DialogDescription>
          Select which sections to re-negotiate and explain why. All team members must vote
          to approve before the session begins.
        </DialogDescription>
      </DialogHeader>

      <div className='space-y-5 py-2'>
        {/* Section selector */}
        <div className='space-y-3'>
          <Label className='text-sm font-medium'>Sections to amend *</Label>
          <div className='space-y-2'>
            {AMENDABLE_SECTIONS.map(({ key, label }) => (
              <div key={key} className='flex items-center gap-2'>
                <Checkbox
                  id={key}
                  checked={selectedSections.includes(key)}
                  onChange={() => toggleSection(key)}
                />
                <label htmlFor={key} className='text-sm text-gray-700 cursor-pointer'>
                  {label}
                </label>
              </div>
            ))}
          </div>
          {selectedSections.length === 0 && (
            <p className='text-xs text-red-500'>Please select at least one section.</p>
          )}
        </div>

        {/* Reason */}
        <div className='space-y-2'>
          <Label htmlFor='amendment-reason' className='text-sm font-medium'>
            Reason for amendment *
          </Label>
          <Textarea
            id='amendment-reason'
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder='Explain why this section needs to be updated (e.g., circumstances have changed, original agreement was unclear)…'
            rows={4}
            className='text-sm resize-none'
          />
          <p className='text-xs text-gray-500'>
            This will be shown to your teammates when they vote.
          </p>
        </div>
      </div>

      <DialogFooter>
        <Button variant='outline' onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!selectedSections.length || !reason.trim() || isSubmitting}
          className='bg-amber-600 hover:bg-amber-700 text-white gap-1.5'
        >
          {isSubmitting ? (
            <>
              <Loader2 className='h-4 w-4 animate-spin' />
              Submitting…
            </>
          ) : (
            'Submit Amendment Request'
          )}
        </Button>
      </DialogFooter>
    </>
  )
}

